import React from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import Image from '../../atoms/Image'
import ListItem from './ListItem'
import User from './User'
import UserAttributeList from './UserAttributeList'
import { MysqlSurveyAnswersEdge, MysqlUsers } from '../../graphqlTypes'

/**
 * 性別
 */
const CHILD_SEX = { 0: 'お子さん', 1: '男の子', 2: '女の子' }

type SEX = keyof typeof CHILD_SEX

/**
 * 妊娠期間
 */
const PREGNANCY_PERIOD = 280

const List = styled.ul<{ compact: boolean }>`
  display: flex;
  justify-content: center;
  flex-direction: column;
  list-style: none;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    flex-direction: ${({ compact }) => (compact ? 'row' : 'column')};
  }
`

const Review = styled.div<{ compact: boolean }>`
  margin: ${({ compact }) => (compact ? '10px 0 0' : '14px 0 0')};
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: start;
    display: flex;
    justify-content: space-between;
  }
`

const ReviewFlexContainer = styled.div<{ filled: boolean }>`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    flex: 1;
    padding: ${({ filled }) => (filled ? 0 : '0 25px 0 0')};
  }
`

const ReviewSummary = styled.div`
  align-items: center;
  display: grid;
  grid-gap: 8px 10px;
  grid-template-columns: 48px auto;
  &:first-of-type {
    margin: 0;
  }
`

const ReviewSummaryLabel = styled.label`
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 12px;
  font-weight: bold;
`

const ReviewSummaryRating = styled(Image)`
  width: 24px;
  height: 24px;
`

const ReviewSummaryText = styled.span`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 12px;
  font-weight: bold;
`

const ReviewComment = styled.p`
  color: ${({ theme }) => theme.palette.font.main};
  font-size: 13px;
  margin: 16px 0 0;
  .ReviewComment__Bold {
    color: ${({ theme }) => theme.palette.primary.main};
    font-weight: bold;
  }
`

const OmitReviewComment = styled(ReviewComment)`
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`

const Label = styled.p`
  font-size: 12px;
  font-weight: bold;
  margin: 10px 0 0;
`

const UserAttributeListContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    width: 320px;
  }
`

/**
 * 年齢表示用のラベルを生成する
 * @param {string} birthday - 誕生日
 * @param {string} now - 起算日
 * @param {number} sex - 性別
 */
function createChildAgeLabel(birthday: string, now: string, sex: SEX): string {
  const born = dayjs(birthday).isBefore(dayjs(now))
  if (born) {
    const { year, month } = toAge(birthday, now)

    return `${year}歳${month}ヶ月の${CHILD_SEX[sex]}`
  }

  const weeks = toPregnancyPastWeeks(birthday, now)

  return `妊娠${weeks}週目の${CHILD_SEX[sex]}`
}

/**
 * 誕生日と起算日から妊娠週数を算出する
 * @param {string} birthday - 誕生日
 * @param {string} now - 起算日
 */
function toPregnancyPastWeeks(birthday: string, now: string): number {
  // NOTE: 妊娠週数は妊娠期間を${PREGNANCY_PERIOD}日として計算する
  const diff = dayjs(birthday).diff(dayjs(now), 'day')
  const past = PREGNANCY_PERIOD - diff

  return Math.floor(past / 7)
}

/**
 * 誕生日と起算日から年齢と月齢を算出する
 * @param {string} birthday - 誕生日
 * @param {string} now - 起算日
 */
function toAge(birthday: string, now: string): { year: number; month: number } {
  const diff = dayjs(now).diff(dayjs(birthday), 'month')

  return {
    year: Math.floor(diff / 12),
    month: diff % 12,
  }
}

/**
 * 本文中の指定の文字列を太字にする
 * @param {string} body - 本文
 * @param {?string[]} targets - 太字にする文字列のリスト
 */
function toBold(body: string, targets: string[] | undefined) {
  return targets
    ? targets.reduce((body, target) => {
        return body.includes(target)
          ? body.split(target).join(`<span class="ReviewComment__Bold">${target}</span>`)
          : body
      }, body)
    : body
}

type Props = {
  basic?: boolean
  category?: number
  className?: string
  compact?: boolean
  highlightWords?: string[]
  omitComment?: boolean
  surveyAnswers: (MysqlSurveyAnswersEdge & { label?: string })[]
}

export default function ReviewList(props: Props) {
  const { category, className, highlightWords, omitComment, surveyAnswers } = props
  const basic = !!props.basic
  const compact = !!props.compact
  const showUserAttribute = category != null

  return (
    <List className={className} compact={compact}>
      {surveyAnswers.map(({ label, node }) => {
        const user = node.users as MysqlUsers
        const job = user.mother_job === '専業主婦' ? '専業主婦' : 'ワーキングママ'
        const userAge = toAge(user.mother_birthday, user.created)
        const username = `${job} (${userAge.year})`

        const youngestChildAge = createChildAgeLabel(
          user.youngest_child_birthday,
          user.created,
          user.youngest_child_sex as SEX
        )

        const otherChildren = user.other_children ? JSON.parse(user.other_children) : []

        const ratings: number[] = JSON.parse(node.ratings!).map(({ value }: { value: number }) => value)
        const sumRating = ratings.reduce((result, value) => result + value, 0)
        const avgRating = Math.ceil((sumRating / ratings.length) * 100) / 100

        return (
          <ListItem basic={basic} compact={compact} key={node.id}>
            <User
              childAge={`${youngestChildAge}${otherChildren.length > 0 ? ' / ' : ''}${otherChildren
                .map(
                  ({ display_birthday, sex }: { display_birthday: string; sex: number }) =>
                    `${display_birthday}の${CHILD_SEX[sex as SEX]}`
                )
                .join('、')}`}
              compact={compact}
              username={username}
            />
            <Review compact={compact}>
              <ReviewFlexContainer filled={!showUserAttribute}>
                <ReviewSummary>
                  <ReviewSummaryLabel>満足度</ReviewSummaryLabel>
                  <ReviewSummaryRating
                    alt={`${username}の満足度`}
                    filename={(() => {
                      if (avgRating >= 4.3) return 'icon-circle-flower.png'
                      if (avgRating >= 3) return 'icon-circle-double.png'
                      return 'icon-circle-single.png'
                    })()}
                  />
                  <ReviewSummaryLabel>使用期間</ReviewSummaryLabel>
                  <ReviewSummaryText>{node.using_time}</ReviewSummaryText>
                </ReviewSummary>
                {omitComment ? (
                  <OmitReviewComment
                    dangerouslySetInnerHTML={{
                      __html: toBold(node.comment!, highlightWords).replace('\n', ' '),
                    }}
                  />
                ) : (
                  <ReviewComment
                    dangerouslySetInnerHTML={{
                      __html: toBold(node.comment!, highlightWords).replace('\n', '<br />'),
                    }}
                  />
                )}
                {label != null && <Label>{label}</Label>}
              </ReviewFlexContainer>
              {showUserAttribute && (
                <UserAttributeListContainer>
                  <UserAttributeList category={category!} user={user} />
                </UserAttributeListContainer>
              )}
            </Review>
          </ListItem>
        )
      })}
    </List>
  )
}
